/*
 * Copyright (c) 2025 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { TableLazyLoadEvent } from 'primeng/table';
import { AuthLog } from '../../shared-auth-logs/auth-log.model';
import { patchState, signalStore, type, withHooks, withMethods, withState } from '@ngrx/signals';
import { setAllEntities, withEntities } from '@ngrx/signals/entities';
import { inject } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, debounceTime, of, switchMap } from 'rxjs';
import { tap } from 'rxjs/operators';
import { mapTableEventToSpecificationEventModel } from '@irian-ro/primeng-utils-module';
import { JsonApiResource, JsonApiListResourceMetadataBase } from '@irian-ro/abstract-resource';
import { OwnedAuthLogsService } from '../owned-auth-logs.service';

type ListState = {
  tableLazyLoadEvent: TableLazyLoadEvent;
  loading: boolean;
  noData: boolean;
  totalRecords: number;
};

const initialState: ListState = {
  tableLazyLoadEvent: {},
  loading: false,
  noData: true,
  totalRecords: 10,
};

export const ownedAuthLogsStore = signalStore(
  { protectedState: false }, withState(initialState),
  withEntities({
    entity: type<AuthLog>(),
    collection: 'authLogs',
  }),

  withMethods((state) => {
    const ownedAuthLogsService = inject(OwnedAuthLogsService);

    return {
      loadOwnedAuthLogs: rxMethod<TableLazyLoadEvent>((lazy$) =>
        lazy$.pipe(
          debounceTime(300),
          tap(() => patchState(state, { loading: true })),
          switchMap((lazyLoadEvent) => {
            return ownedAuthLogsService.findAll(mapTableEventToSpecificationEventModel(lazyLoadEvent)).pipe(
              catchError(() => {
                patchState(state, { loading: false });
                const emptyData: JsonApiResource<AuthLog[], JsonApiListResourceMetadataBase> = new JsonApiResource();
                emptyData.data = [];
                emptyData.meta = { totalSize: 0 };
                return of(emptyData);
              }),
              tap((res: JsonApiResource<AuthLog[], JsonApiListResourceMetadataBase>) => {
                patchState(state, { loading: false });
                patchState(state, { noData: res.meta.totalSize === 0 });
                patchState(state, { totalRecords: res.meta.totalSize });

                patchState(state, setAllEntities(res.data, { selectId: (item: AuthLog) => item.uuid, collection: 'authLogs' }));
              })
            );
          })
        )
      ),
      patchLazyLoad(lazyLoadEvent: TableLazyLoadEvent) {
        patchState(state, { tableLazyLoadEvent: lazyLoadEvent });
      },
    };
  }),

  withHooks({
    onInit: (store) => {
      store.loadOwnedAuthLogs(store.tableLazyLoadEvent);
    },
  })
);
